import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import * as d3 from 'd3';
import { getDifficultyStyle, getPurposeStyle, getSourceInfo, formatSearchVolume, intentTypes, lightenColor, difficultyRanges, purposeTypes } from '@/shared/utils/constants';

interface KeywordData {
  keyword: string;
  difficalty?: number | null;
  sv?: number | null;
  intent?: string[];
  purpose?: string[];
  source?: string;
  len?: number;
  parent_keyword?: string;
  [key: string]: any;
}

interface TreeViewProps {
  data: KeywordData[];
  groupBy: string;
  quickFilterText: string;
  language: 'fa' | 'en';
  isDark: boolean;
  isRTL: boolean;
  translations: any;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onDownloadSVGRef?: (fn: () => void) => void;
}

interface TreeNodeData {
  name: string;
  value: number;
  fill: string;
  groupKey: string;
  keywords: KeywordData[];
  stats?: {
    totalKeywords: number;
    avgDifficulty: number;
    totalSearchVolume: number;
    avgSearchVolume: number;
  };
  keyword?: string;
  difficulty?: number | null;
  searchVolume?: number | null;
  intent?: string[];
  purpose?: string[];
  length?: number;
  parentKeyword?: string;
  source?: string;
  parent?: string;
  children?: TreeNodeData[];
}

const TreeView: React.FC<TreeViewProps> = ({ data, groupBy, quickFilterText, language, isDark, isRTL, isFullscreen, onToggleFullscreen, onDownloadSVGRef }) => {
  const [hoveredNode, setHoveredNode] = useState<TreeNodeData | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const fullscreenSvgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const fullscreenRef = useRef<HTMLDivElement>(null);

  // Filter data based on quickFilterText (same logic as KeywordDataView)
  const filteredData = useMemo(() => {
    if (!quickFilterText || quickFilterText.trim() === '') return data;
    const filterText = quickFilterText.toLowerCase().trim();
    return data.filter(item => {
      // Search in keyword
      if (item.keyword && item.keyword.toLowerCase().includes(filterText)) {
        return true;
      }
      // Search in parent_keyword
      if (item.parent_keyword && item.parent_keyword.toLowerCase().includes(filterText)) {
        return true;
      }
      // Search in source
      if (item.source && item.source.toLowerCase().includes(filterText)) {
        return true;
      }
      // Search in intent array
      if (Array.isArray(item.intent) && item.intent.some(i => i.toLowerCase().includes(filterText))) {
        return true;
      }
      // Search in purpose array
      if (Array.isArray(item.purpose) && item.purpose.some(p => p.toLowerCase().includes(filterText))) {
        return true;
      }
      return false;
    });
  }, [data, quickFilterText]);

  // Group data based on groupBy (same logic as ChartView)
  const groupedData = useMemo<Record<string, KeywordData[]>>(() => {
    const groups: Record<string, KeywordData[]> = {};
    
    filteredData.forEach(item => {
      let groupKey = 'other';
      
      if (groupBy === 'intent') {
        if (item.intent && Array.isArray(item.intent) && item.intent.length > 0) {
          groupKey = item.intent[0];
        } else {
          groupKey = 'no-intent';
        }
      } else if (groupBy === 'difficulty') {
        if (item.difficalty !== null && item.difficalty !== undefined) {
          const style = getDifficultyStyle(item.difficalty);
          groupKey = style ? style.label : 'unknown';
        } else {
          groupKey = 'no-difficulty';
        }
      } else if (groupBy === 'searchVolume') {
        if (item.sv !== null && item.sv !== undefined) {
          if (item.sv < 1000) {
            groupKey = language === 'fa' ? 'کم (زیر 1000)' : 'Low (< 1K)';
          } else if (item.sv < 10000) {
            groupKey = language === 'fa' ? 'متوسط (1K-10K)' : 'Medium (1K-10K)';
          } else if (item.sv < 100000) {
            groupKey = language === 'fa' ? 'زیاد (10K-100K)' : 'High (10K-100K)';
          } else {
            groupKey = language === 'fa' ? 'خیلی زیاد (بالای 100K)' : 'Very High (> 100K)';
          }
        } else {
          groupKey = 'no-volume';
        }
      } else if (groupBy === 'length') {
        groupKey = `${item.len || 0} ${language === 'fa' ? 'کلمه' : 'words'}`;
      } else if (groupBy === 'purpose') {
        if (item.purpose && Array.isArray(item.purpose) && item.purpose.length > 0) {
          const purposeConfig = getPurposeStyle(item.purpose[0]);
          groupKey = purposeConfig ? (language === 'fa' ? purposeConfig.label : purposeConfig.enLabel) : item.purpose[0];
        } else {
          groupKey = 'no-purpose';
        }
      } else if (groupBy === 'source') {
        if (item.source) {
          const sourceConfig = getSourceInfo(item.source);
          groupKey = sourceConfig ? (language === 'fa' ? sourceConfig.fa : sourceConfig.en) : item.source;
        } else {
          groupKey = 'no-source';
        }
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });
    
    return groups;
  }, [filteredData, groupBy, language]);

  // Helper function to get purpose key from label
  const getPurposeKeyFromLabel = (label: string): string | null => {
    for (const [key, config] of Object.entries(purposeTypes)) {
      if (config.label === label || config.enLabel === label) {
        return key;
      }
    }
    return null;
  };

  // Helper function to get difficulty range from label
  const getDifficultyRangeFromLabel = (label: string) => {
    return difficultyRanges.find(range => 
      range.label === label || range.enLabel === label
    );
  };

  // Get group label
  const getGroupLabel = (groupKey: string): string => {
    if (groupBy === 'intent') {
      const intent = intentTypes[groupKey];
      if (intent) {
        return language === 'fa' ? intent.label : intent.enLabel;
      }
      return groupKey === 'no-intent' ? (language === 'fa' ? 'بدون نیت' : 'No Intent') : groupKey;
    } else if (groupBy === 'difficulty') {
      return groupKey === 'no-difficulty' ? (language === 'fa' ? 'بدون سختی' : 'No Difficulty') : groupKey;
    } else if (groupBy === 'searchVolume') {
      return groupKey === 'no-volume' ? (language === 'fa' ? 'بدون حجم جستجو' : 'No Search Volume') : groupKey;
    } else if (groupBy === 'length') {
      return groupKey;
    } else if (groupBy === 'purpose') {
      return groupKey === 'no-purpose' ? (language === 'fa' ? 'بدون هدف' : 'No Purpose') : groupKey;
    } else if (groupBy === 'source') {
      return groupKey === 'no-source' ? (language === 'fa' ? 'بدون منبع' : 'No Source') : groupKey;
    }
    return groupKey;
  };

  // Helper function to get color based on groupBy and groupKey
  const getGroupColor = (groupBy: string, groupKey: string): { parent: string; child: string } => {
    if (groupBy === 'difficulty') {
      // Use difficulty colors - bgColor for parent nodes (light version)
      const difficultyRange = getDifficultyRangeFromLabel(groupKey);
      if (difficultyRange) {
        return {
          parent: difficultyRange.bgColor, // Light color for parent nodes
          child: lightenColor(difficultyRange.bgColor, 30) // Lighter for child nodes
        };
      }
      // Fallback for no-difficulty
      return {
        parent: '#f3f4f6',
        child: lightenColor('#f3f4f6', 30)
      };
    } else if (groupBy === 'intent') {
      // Use intent colors - bgColor for parent nodes
      const intent = intentTypes[groupKey];
      if (intent) {
        return {
          parent: intent.bgColor,
          child: lightenColor(intent.bgColor, 30)
        };
      }
      // Fallback for no-intent
      return {
        parent: '#f3f4f6',
        child: lightenColor('#f3f4f6', 30)
      };
    } else if (groupBy === 'purpose') {
      // Use purpose colors - bgColor for parent nodes
      const purposeKey = getPurposeKeyFromLabel(groupKey);
      const purpose = purposeKey ? purposeTypes[purposeKey] : null;
      if (purpose) {
        return {
          parent: purpose.bgColor,
          child: lightenColor(purpose.bgColor, 30)
        };
      }
      // Fallback for no-purpose
      return {
        parent: '#f3f4f6',
        child: lightenColor('#f3f4f6', 30)
      };
    } else if (groupBy === 'source') {
      // Use source colors - create colors based on source
      const sourceInfo = getSourceInfo(groupKey);
      if (sourceInfo) {
        // Generate a color based on source name hash
        const hash = groupKey.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const hue = hash % 360;
        const baseColor = `hsl(${hue}, 70%, 85%)`;
        return {
          parent: baseColor,
          child: lightenColor(baseColor, 30)
        };
      }
    }
    
    // Default colors for other groupBy types
    const defaultColors = ['#c4b5fd', '#fdba74', '#fde68a', '#86efac', '#93c5fd', '#f9a8d4', '#c4b5fd', '#5eead4', '#fcd34d', '#6ee7b7'];
    const colorIndex = groupKey.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % defaultColors.length;
    const baseColor = defaultColors[colorIndex];
    return {
      parent: baseColor,
      child: lightenColor(baseColor, 40)
    };
  };

  // Prepare hierarchical data for Tree
  const treeData = useMemo<TreeNodeData>(() => {
    const groups = Object.entries(groupedData);
    
    const children = groups.map(([groupKey, keywords]) => {
      const totalKeywords = keywords.length;
      const avgDifficulty = keywords.reduce((sum, k) => sum + (k.difficalty || 0), 0) / totalKeywords || 0;
      const totalSearchVolume = keywords.reduce((sum, k) => sum + (k.sv || 0), 0);
      const avgSearchVolume = totalSearchVolume / totalKeywords || 0;

      // Get colors based on groupBy
      const colors = getGroupColor(groupBy, groupKey);
      const groupColor = colors.parent; // Light color for parent nodes
      const lightenedColor = colors.child; // Lighter for child nodes

      return {
        name: getGroupLabel(groupKey),
        value: totalKeywords,
        fill: groupColor, // Light color for parent nodes
        groupKey,
        keywords,
        stats: {
          totalKeywords,
          avgDifficulty: Math.round(avgDifficulty),
          totalSearchVolume,
          avgSearchVolume: Math.round(avgSearchVolume),
        },
        children: keywords.map((item) => ({
          name: item.keyword,
          value: 1,
          fill: lightenedColor, // Lighter version for child nodes
          keyword: item.keyword,
          difficulty: item.difficalty,
          searchVolume: item.sv,
          intent: item.intent,
          purpose: item.purpose,
          length: item.len,
          parentKeyword: item.parent_keyword,
          source: item.source,
          parent: getGroupLabel(groupKey),
          keywords: [item],
          groupKey: '',
        }))
      };
    });

    return {
      name: 'root',
      value: 0,
      fill: '#fff',
      groupKey: '',
      keywords: [],
      children: children
    };
  }, [groupedData, language, groupBy]);

  // Helper function to draw tree on any SVG element
  const drawTree = useCallback((svgElement: SVGSVGElement | null, width: number, height: number) => {
    if (!svgElement) return;

    // Clear previous content
    d3.select(svgElement).selectAll('*').remove();

    const svg = d3.select(svgElement)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g')
      .attr('transform', `translate(40,20)`);

    // Create tree layout
    const treeLayout = d3.tree<TreeNodeData>()
      .size([height - 40, width - 200]);

    // Create hierarchy
    const root = d3.hierarchy(treeData)
      .sum(d => d.value);

    // Compute the tree layout
    treeLayout(root as any);

    // Create links generator
    const linkGenerator = d3.linkHorizontal<any, any>()
      .x((d: any) => d.y)
      .y((d: any) => d.x);

    // Draw links
    g.selectAll('.link')
      .data(root.links())
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('d', linkGenerator)
      .attr('fill', 'none')
      .attr('stroke', isDark ? '#4b5563' : '#9ca3af')
      .attr('stroke-width', 2);

    // Create nodes
    const nodes = g.selectAll('.node')
      .data(root.descendants())
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', (d: any) => `translate(${d.y},${d.x})`);

    // Draw circles for nodes
    nodes.append('circle')
      .attr('r', (d: any) => d.depth === 0 ? 0 : (d.depth === 1 ? 12 : 8))
      .attr('fill', (d: any) => d.data.fill || '#ccc')
      .attr('stroke', isDark ? '#1f2937' : '#ffffff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseover', function(_event: any, d: any) {
        d3.select(this)
          .attr('stroke-width', 3)
          .attr('r', (d: any) => d.depth === 0 ? 0 : (d.depth === 1 ? 14 : 10));
        setHoveredNode(d.data);
      })
      .on('mouseout', function() {
        d3.select(this)
          .attr('stroke-width', 2)
          .attr('r', (d: any) => d.depth === 0 ? 0 : (d.depth === 1 ? 12 : 8));
        setHoveredNode(null);
      });

    // Add icons for purpose groupBy (parent nodes only) - next to label
    if (groupBy === 'purpose') {
      nodes.filter((d: any) => d.depth === 1).append('text')
        .attr('class', 'group-icon')
        .attr('dy', -18) // Same vertical position as label
        .attr('dx', isRTL ? 7 : -7) // Position icon: right side for RTL, left side for LTR
        .attr('text-anchor', 'middle')
        .attr('font-size', '18px')
        .text((d: any) => {
          const purposeKey = getPurposeKeyFromLabel(d.data.groupKey);
          const purposeConfig = purposeKey ? purposeTypes[purposeKey] : null;
          return purposeConfig && purposeConfig.icon ? purposeConfig.icon : '';
        });
    }

    // Add labels
    nodes.append('text')
      .attr('dy', (d: any) => d.depth === 0 ? 0 : (d.depth === 1 ? -18 : -12))
      .attr('dx', (d: any) => {
        // Adjust horizontal position if icon is present for purpose groupBy
        if (groupBy === 'purpose' && d.depth === 1) {
          // For RTL: icon is at +7 (right), label anchor at -7 with start anchor (text extends right, icon at right of text)
          // For LTR: icon is at -7 (left), label anchor at +7 with start anchor (text extends right, icon at left of text)
          return isRTL ? -7 : 7; // Label anchor position
        }
        return d.depth === 0 ? 0 : (d.depth === 1 ? 0 : 12);
      })
      .attr('text-anchor', (d: any) => {
        // For purpose groupBy with icons
        if (groupBy === 'purpose' && d.depth === 1) {
          // RTL: start anchor at -7 means text extends right from anchor, so icon at +7 is at right of text
          // LTR: start anchor at +7 means text extends right from anchor, so icon at -7 is at left of text
          return 'start'; // Both use start anchor
        }
        return d.depth === 0 ? 'middle' : (d.depth === 1 ? 'middle' : 'start');
      })
      .attr('fill', isDark ? '#ffffff' : '#000000')
      .attr('font-size', (d: any) => d.depth === 1 ? '14px' : '12px')
      .attr('font-weight', (d: any) => d.depth === 1 ? '600' : '500')
      .text((d: any) => {
        const name = d.data.name || d.data.keyword;
        if (d.depth === 1) {
          return name.length > 20 ? `${name.substring(0, 20)}...` : name;
        } else if (d.depth === 2) {
          return name.length > 15 ? `${name.substring(0, 15)}...` : name;
        }
        return name;
      });
  }, [treeData, isDark, groupBy, isRTL]);

  // Download SVG function
  const downloadSVG = useCallback(() => {
    const svgElement = isFullscreen ? fullscreenSvgRef.current : svgRef.current;
    if (!svgElement) return;
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = `keywordyab-tree-${groupBy}-${Date.now()}.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(svgUrl);
  }, [isFullscreen, groupBy]);

  // Expose downloadSVG to parent via callback ref
  useEffect(() => {
    if (onDownloadSVGRef) {
      onDownloadSVGRef(downloadSVG);
    }
  }, [downloadSVG, onDownloadSVGRef]);

  // Draw tree for normal view
  useEffect(() => {
    if (!svgRef.current || isFullscreen) return;
    drawTree(svgRef.current, 1200, 800);
  }, [drawTree, isFullscreen]);

  // Draw tree for fullscreen view
  useEffect(() => {
    if (!fullscreenSvgRef.current || !isFullscreen) return;
    const width = Math.min(window.innerWidth - 100, 1800);
    const height = Math.min(window.innerHeight - 150, 1200);
    drawTree(fullscreenSvgRef.current, width, height);
  }, [drawTree, isFullscreen]);

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        onToggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isFullscreen, onToggleFullscreen]);

  return (
    <>
      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          ref={fullscreenRef}
          className="fixed inset-0 z-[999999] bg-white dark:bg-gray-900 flex flex-col items-center justify-center p-8"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* Close Button */}
          <button
            onClick={onToggleFullscreen}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} p-2 rounded-md transition-colors ${
              isDark
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-tooltip-id="action-tooltip"
            data-tooltip-content={language === 'fa' ? 'بستن' : 'Close'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Fullscreen Tree */}
          <div className="w-full h-full flex flex-col items-center justify-center">
            <div className="flex-1 flex items-center justify-center w-full overflow-auto">
              <svg ref={fullscreenSvgRef} style={{ maxWidth: '100%', maxHeight: '100%' }}></svg>
            </div>
          </div>
        </div>
      )}

      {/* Normal View */}
      <div className={`w-full ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="flex flex-col items-center justify-center min-h-[800px]">
          {/* Tree Visualization */}
          <div className="w-full max-w-7xl mb-8">
            <div className="flex justify-center overflow-auto">
              <svg ref={svgRef}></svg>
            </div>
          </div>

          {/* Tooltip */}
          {hoveredNode && (
            <div
              ref={tooltipRef}
              className={`fixed p-4 rounded-lg shadow-lg border pointer-events-none z-50 ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <p className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {hoveredNode.keyword || hoveredNode.name}
              </p>
              <div className={`text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {hoveredNode.stats && (
                  <>
                    <p>{language === 'fa' ? 'تعداد کلمات:' : 'Total Keywords:'} {hoveredNode.stats.totalKeywords}</p>
                    {hoveredNode.stats.avgDifficulty > 0 && (
                      <p>{language === 'fa' ? 'میانگین سختی:' : 'Avg Difficulty:'} {hoveredNode.stats.avgDifficulty}</p>
                    )}
                    {hoveredNode.stats.avgSearchVolume > 0 && (
                      <p>{language === 'fa' ? 'میانگین حجم جستجو:' : 'Avg Search Volume:'} {formatSearchVolume(hoveredNode.stats.avgSearchVolume)}</p>
                    )}
                  </>
                )}
                {hoveredNode.keyword && (
                  <>
                    {hoveredNode.difficulty !== null && hoveredNode.difficulty !== undefined && (
                      <p>{language === 'fa' ? 'سختی:' : 'Difficulty:'} {hoveredNode.difficulty}</p>
                    )}
                    {hoveredNode.searchVolume !== null && hoveredNode.searchVolume !== undefined && (
                      <p>{language === 'fa' ? 'حجم جستجو:' : 'Search Volume:'} {formatSearchVolume(hoveredNode.searchVolume)}</p>
                    )}
                    {hoveredNode.intent && Array.isArray(hoveredNode.intent) && hoveredNode.intent.length > 0 && (
                      <p>{language === 'fa' ? 'نیت:' : 'Intent:'} {hoveredNode.intent.map(i => {
                        const intent = intentTypes[i];
                        return intent ? (language === 'fa' ? intent.label : intent.enLabel) : i;
                      }).join(', ')}</p>
                    )}
                    {hoveredNode.purpose && Array.isArray(hoveredNode.purpose) && hoveredNode.purpose.length > 0 && (
                      <p>{language === 'fa' ? 'هدف:' : 'Purpose:'} {hoveredNode.purpose.map(p => {
                        const purpose = getPurposeStyle(p);
                        return purpose ? (language === 'fa' ? purpose.label : purpose.enLabel) : p;
                      }).join(', ')}</p>
                    )}
                    {hoveredNode.parent && (
                      <p>{language === 'fa' ? 'گروه:' : 'Group:'} {hoveredNode.parent}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TreeView;


